import { useQueryClient, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { translate } from "shared/languages";
import { ResponseHandler } from "shared/types/axios";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { invalidateCustomerList } from "hooks/mutations/customerList";
import { LaterBooking } from "shared/types";
import { VoidReason } from "shared/types/enum";
import { laterBookingsOf, usedSessionsOf } from "shared/utils/windowConflict";

export const upgradeCustomerPackage = (
  previousCustomerPackageId: string,
  customerId: string,
  packageId: string,
  paymentMethodId?: string | null,
) => {
  return client.post(`/v1/customer_package/upgrade`, {
    previous_customer_package_id: previousCustomerPackageId,
    customer_id: customerId,
    package_id: packageId,
    payment_method_id: paymentMethodId ?? null,
  });
};

type UpdateCustomerPackageForm = {
  packageId: string;
  expiry_date?: string;
  // undefined = not submitted (leave unchanged), null = explicit clear,
  // a string = the new payment method.
  payment_method_id?: string | null;
  // The admin confirmed cancelling the bookings after the new end date.
  cancel_later_bookings?: boolean;
};

// onLaterBookings: an end-date edit refused because bookings come after the
// new date; the caller lists them for confirmation and retries with
// cancel_later_bookings instead of showing the refusal as a toast.
export const useUpdateCustomerPackage = (
  onLaterBookings?: (bookings: LaterBooking[], form: UpdateCustomerPackageForm) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      packageId,
      expiry_date,
      payment_method_id,
      cancel_later_bookings,
    }: UpdateCustomerPackageForm) => {
      return client.put(`/v1/customer_package/${packageId}`, {
        expiry_date,
        payment_method_id,
        cancel_later_bookings,
      });
    },
    onSuccess: ({ data: response }, variables) => {
      const customerId = response.data.customer_id;
      const customerPackageId = response.data.id;
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`user`, customerId] });
      queryClient.invalidateQueries({ queryKey: [`customer_package`, customerPackageId] });
      invalidateCustomerList(queryClient);
      if (variables.cancel_later_bookings) {
        queryClient.invalidateQueries({ queryKey: ["booking-v2"] });
        queryClient.invalidateQueries({ queryKey: ["waitlist-v2"] });
        queryClient.invalidateQueries({ queryKey: ["customer_package"] });
      }
      const fieldLabel =
        variables.payment_method_id !== undefined
          ? translate("paymentMethods.col_payment_method")
          : translate("label.expiry_date");
      getApiSuccessMessage(`${fieldLabel} ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>, form) => {
      const later = laterBookingsOf(error);
      if (later && onLaterBookings && !form.cancel_later_bookings) {
        onLaterBookings(later, form);
        return;
      }
      getApiErrorMessage(error);
    },
  });
};

export const useUpdateCustomerPackageCredits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ packageId, remaining_credits }: { packageId: string; remaining_credits: number }) => {
      return client.put(`/v1/customer_package/${packageId}`, { remaining_credits });
    },
    onSuccess: ({ data: response }) => {
      const customerId = response.data.customer_id;
      const customerPackageId = response.data.id;
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`user`, customerId] });
      queryClient.invalidateQueries({ queryKey: [`customer_package`, customerPackageId] });
      invalidateCustomerList(queryClient);
      getApiSuccessMessage(`${translate("label.remaining_session")} ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// voucherAction: what happens to a loyalty/referral discount voucher spent on
// this sale ("return" it to the client, or "void" it). Omitted when the sale
// used none.
export type SpentVoucherAction = "return" | "void";

export type VoidCustomerPackageForm = {
  customerPackageId: string;
  customerId: string;
  reason: VoidReason;
  note?: string;
  voucherAction?: SpentVoucherAction;
  // Set on the retry after the server answered void_package_used_confirm:
  // the admin has seen how many sessions become complimentary.
  acknowledgeUsedSessions?: boolean;
};

export const voidCustomerPackage = ({
  customerPackageId,
  reason,
  note,
  voucherAction,
  acknowledgeUsedSessions,
}: VoidCustomerPackageForm): Promise<ResponseHandler> => {
  return client
    .put(`/v1/customer_package/${customerPackageId}/void`, {
      reason,
      ...(note ? { note } : {}),
      ...(voucherAction ? { voucher_action: voucherAction } : {}),
      ...(acknowledgeUsedSessions ? { acknowledge_used_sessions: true } : {}),
    })
    .then((res) => res.data);
};

export const unvoidCustomerPackage = (
  customerPackageId: string,
): Promise<ResponseHandler> => {
  return client
    .put(`/v1/customer_package/${customerPackageId}/unvoid`, {})
    .then((res) => res.data);
};

// A void changes what every one of these reads: the package itself and its
// status log, the client's Paket and Transaksi tabs, every booking row that
// spends from it (their action buttons freeze), and every money figure.
const invalidateAfterVoid = (
  queryClient: ReturnType<typeof useQueryClient>,
  customerId: string,
) => {
  // The prefix, not one id: the tab list key is
  // ["customer_package","customer",customerId] and the pickers have their own.
  queryClient.invalidateQueries({ queryKey: ["customer_package"] });
  queryClient.invalidateQueries({ queryKey: ["booking-v2"] });
  queryClient.invalidateQueries({ queryKey: ["waitlist-v2"] });
  queryClient.invalidateQueries({ queryKey: ["user", customerId] });
  queryClient.invalidateQueries({ queryKey: ["analytics"] });
  // The whole "business" family, not only the dashboard: the Monthly
  // Statement of Account drops the voided sale too.
  queryClient.invalidateQueries({ queryKey: ["business"] });
  // The settings list's Terpakai column releases the code's use.
  queryClient.invalidateQueries({ queryKey: ["discount-codes"] });
  queryClient.invalidateQueries({ queryKey: ["notification"] });
  // A void (and its undo) moves vouchers and the referral: the discount
  // voucher spent on the sale, the referral it converted (back to pending,
  // its reward vouchers voided) and so the referrer's Reward tab and the
  // Pengaturan pending-referral table. Prefixes, because the referrer is a
  // different customer id than the one being voided.
  queryClient.invalidateQueries({ queryKey: ["loyalty"] });
  queryClient.invalidateQueries({ queryKey: ["referral"] });
  invalidateCustomerList(queryClient);
};

// onUsedSessions: the sale already has taught sessions, so the void needs an
// explicit acknowledgment. The caller reopens its modal showing the count
// (keeping the reason and note already typed) and retries.
export const useVoidCustomerPackage = (
  onUsedSessions?: (usedSessions: number, form: VoidCustomerPackageForm) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: voidCustomerPackage,
    onSuccess: (_data, variables) => {
      invalidateAfterVoid(queryClient, variables.customerId);
      getApiSuccessMessage(translate("messages.success.void_customer_package"));
    },
    onError: (error: AxiosError<ErrorResponse>, form) => {
      const used = usedSessionsOf(error);
      if (used !== null && onUsedSessions && !form.acknowledgeUsedSessions) {
        onUsedSessions(used, form);
        return;
      }
      getApiErrorMessage(error);
    },
  });
};

export const useUnvoidCustomerPackage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerPackageId }: { customerPackageId: string; customerId: string }) =>
      unvoidCustomerPackage(customerPackageId),
    onSuccess: (_data, variables) => {
      invalidateAfterVoid(queryClient, variables.customerId);
      getApiSuccessMessage(translate("messages.success.undo_void_customer_package"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Cancel Upgrade: the id is the NEW half's; the server voids it and restores
// the old package. No body (the reason is fixed server-side).
const cancelUpgrade = (customerPackageId: string) =>
  client.put(`/v1/customer_package/${customerPackageId}/cancel-upgrade`);

export const useCancelUpgrade = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerPackageId }: { customerPackageId: string; customerId: string }) =>
      cancelUpgrade(customerPackageId),
    onSuccess: (_data, variables) => {
      // Everything a void touches, plus the upgrade picker: the old package
      // is upgradable again, and its target list is cached under this key.
      invalidateAfterVoid(queryClient, variables.customerId);
      queryClient.invalidateQueries({ queryKey: ["package", "upgrade"] });
      getApiSuccessMessage(translate("messages.success.cancel_upgrade"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};
